import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, EmptyState, Modal } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCreateCouponMutation, useUpdateCouponMutation, useCouponsQuery } from '../api';
import { CouponForm } from '../components/CouponForm';
import { CouponTable, couponToFormValues } from '../components/CouponTable';
import { CouponUsageDrawer } from '../components/CouponUsageDrawer';
import type { CouponFormValues } from '../schemas';
import type { Coupon } from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { useActiveShop } from '@/features/shops/use-active-shop';

/**
 * `[PROVISOIRE — OQ-25]` — RBAC déjà tranché avant l'OQ elle-même :
 * `merchant_owner` uniquement, aucun drapeau Staff configurable. Ce garde
 * client-side est un confort d'affichage, pas la vraie frontière de
 * sécurité — celle-ci est `@RequireRole('merchant_owner')` côté serveur sur
 * les quatre routes ; un compte Staff qui contournerait ce garde recevrait
 * quand même une 403 réelle de l'API, jamais de données.
 */
export function PromotionListPage(): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const shop = useActiveShop();
  const couponsQuery = useCouponsQuery();
  const createMutation = useCreateCouponMutation();
  const updateMutation = useUpdateCouponMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [usagesCoupon, setUsagesCoupon] = useState<Coupon | null>(null);

  const isOwner = shop.activeMembership?.roleKey === 'merchant_owner';

  function openCreate(): void {
    setFormError(null);
    setEditingCoupon(null);
    setModalMode('create');
  }

  /**
   * Dashboard's "Nouveau coupon" quick action links here with `?new=1`
   * (QuickActions.tsx) rather than duplicating this modal on the
   * Dashboard — the query param is consumed once, then stripped, so a
   * later refresh/back-navigation on this page never re-opens the modal
   * unexpectedly.
   */
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      openCreate();
      setSearchParams((params) => {
        params.delete('new');
        return params;
      });
    }
  }, [searchParams, setSearchParams]);

  function openEdit(coupon: Coupon): void {
    setFormError(null);
    setEditingCoupon(coupon);
    setModalMode('edit');
  }

  function closeModal(): void {
    setModalMode(null);
    setEditingCoupon(null);
  }

  async function onSubmit(values: CouponFormValues): Promise<void> {
    setFormError(null);
    try {
      if (modalMode === 'edit' && editingCoupon !== null) {
        await updateMutation.mutateAsync({ couponId: editingCoupon.id, values });
        toast.success(t('merchant.promotions.updated'));
      } else {
        await createMutation.mutateAsync(values);
        toast.success(t('merchant.promotions.created'));
      }
      closeModal();
    } catch (error) {
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  async function onToggleActive(coupon: Coupon): Promise<void> {
    setTogglingId(coupon.id);
    try {
      await updateMutation.mutateAsync({
        couponId: coupon.id,
        values: couponToFormValues({ ...coupon, isActive: !coupon.isActive }),
      });
      toast.success(
        coupon.isActive
          ? t('merchant.promotions.deactivated')
          : t('merchant.promotions.reactivated'),
      );
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    } finally {
      setTogglingId(null);
    }
  }

  if (!isOwner && !shop.isLoading) {
    return (
      <EmptyState
        title={t('merchant.staff.ownerOnlyTitle')}
        description={t('merchant.promotions.ownerOnlyDescription')}
      />
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">
            {t('merchant.nav.promotions')}
          </h1>
          <p className="text-sm text-inkFaint">{t('merchant.promotions.subtitle')}</p>
        </div>
        <Button onClick={openCreate}>{t('merchant.dashboard.quickActions.newCoupon')}</Button>
      </div>

      {couponsQuery.isPending || shop.isLoading ? (
        <TableSkeleton columns={6} />
      ) : couponsQuery.isError ? (
        <EmptyState
          title={t('merchant.promotions.loadError')}
          description={t(resolveErrorMessageKey(couponsQuery.error))}
        />
      ) : couponsQuery.data.length === 0 ? (
        <EmptyState
          title={t('merchant.promotions.emptyTitle')}
          description={t('merchant.promotions.emptyDescription')}
          action={
            <Button onClick={openCreate}>{t('merchant.dashboard.quickActions.newCoupon')}</Button>
          }
        />
      ) : (
        <CouponTable
          coupons={couponsQuery.data}
          onEdit={openEdit}
          onToggleActive={(coupon) => {
            void onToggleActive(coupon);
          }}
          onViewUsages={setUsagesCoupon}
          togglingId={togglingId}
        />
      )}

      <Modal
        open={modalMode !== null}
        title={
          modalMode === 'edit'
            ? t('merchant.promotions.editTitle')
            : t('merchant.dashboard.quickActions.newCoupon')
        }
        onClose={closeModal}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              {t('merchant.common.cancel')}
            </Button>
            <Button type="submit" form="coupon-form" loading={isSubmitting}>
              {modalMode === 'edit'
                ? t('merchant.common.save')
                : t('merchant.categories.createAction')}
            </Button>
          </>
        }
      >
        {formError !== null ? (
          <p
            role="alert"
            className="mb-3 rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical"
          >
            {formError}
          </p>
        ) : null}
        <CouponForm
          formId="coupon-form"
          {...(editingCoupon !== null ? { defaultValues: couponToFormValues(editingCoupon) } : {})}
          onSubmit={(values) => {
            void onSubmit(values);
          }}
          isSubmitting={isSubmitting}
        />
      </Modal>

      <CouponUsageDrawer
        coupon={usagesCoupon}
        onClose={() => {
          setUsagesCoupon(null);
        }}
      />
    </div>
  );
}
